import { Alert } from "@mui/material";
import { useEffect, useMemo } from "react";
import { createUseStyles } from "react-jss";
import { theme } from "$utils/theme/bataillog";

import useEDBIntegratedOrdersList from "$hooks/useEDBIntegratedOrdersList";
import { useList } from "$hooks/useList";
import { Order, Reception, SuborderProduct } from "$types";
import { Services } from "$utils/typeConstants";
import { OrderProduct } from "$types/EDBIntegratedOrders";
import useAdvices, {
  ALL_EQUALS_ADVICE,
  PARTIAL_RECEPTION_ADVICE,
  SUBORDER_ADVICES,
} from "$hooks/useAdvices";
import useAuth from "$hooks/useAuth";
import { normalizeArray } from "$utils/utils";

const {
  ALL_PRODUCTS_LESS_QUANTITIES,
  ALL_PRODUCTS_SAME_QUANTITIES,
  LESS_QUANTITES_CL_MULTIPLE_SELECTED,
  SAME_QUANTITIES_CL_MULTIPLE_SELECTED,
  SOME_PRODUCTS_LESS_QUANTITIES,
  SOME_PRODUCTS_SAME_QUANTITIES,
} = SUBORDER_ADVICES;

type Props = {
  order: Order;
  reception: Reception;
  selectedSuborders: string[];
};

function RefTypeAdvice({ reception, order, selectedSuborders }: Props) {
  const classes = useStyles();
  const { token } = useAuth();
  const { suborders } = order;
  const { advices, getAdvice, upsert: upsertAdvice } = useAdvices([]);

  const { edbsIntegratedOrders } = useEDBIntegratedOrdersList(suborders);

  const { data: receptionsData = [] } = useList<Reception & { id: number }>(
    "api/order/receptions/consolidated",
    {
      defaultFilters: {
        id: { $ne: reception.id },
        receptionDate: { $lte: reception.receptionDate },
        // Change this structure to generate nested brackets
        receptionReferences: {
          orders: order.id,
        },
      },
      defaultSort: {
        field: "receptionDate",
        direction: "asc",
      },
      defaultPageSize: 1,
      populate: ["receptionReferences", "receptionReferences.orders"],
      service: Services.GATEWAY_API,
      jwt: token,
    }
  );

  // Normalize the response data to ensure we always have an array
  const normalizedReceptions = useMemo(() => {
    return normalizeArray(receptionsData);
  }, [receptionsData]);

  const filteredReceptions = useMemo(() => {
    return normalizedReceptions.filter((receptionItem) => {
      const receptionRefs = normalizeArray(receptionItem.receptionReferences);

      return receptionRefs.some((ref) => {
        const orders = normalizeArray(ref.orders);

        return orders.some((refOrder) => refOrder.id === order.id);
      });
    });
  }, [normalizedReceptions, order.id]);

  // Get the first relevant reception after filtering
  const firstReception = useMemo(() => {
    return filteredReceptions.length > 0 ? filteredReceptions[0] : null;
  }, [filteredReceptions]);

  useEffect(() => {
    const hasProductIntegratedWithSameQty = (
      integratedProducts: OrderProduct[],
      suborderProduct: SuborderProduct
    ): boolean => {
      const integratedProduct = integratedProducts.find(
        (ip) => ip.suborderProductId === suborderProduct.id
      );
      if (!integratedProduct) return false;
      return (
        integratedProducts &&
        integratedProduct.quantity === suborderProduct.quantity
      );
    };

    const hasProductIntegratedWithLessQty = (
      integratedProducts: OrderProduct[],
      suborderProduct: SuborderProduct
    ): boolean => {
      const integratedProduct = integratedProducts.find(
        (ip) => ip.suborderProductId === suborderProduct.id
      );
      if (!integratedProduct) return false;
      return (
        integratedProducts &&
        integratedProduct.quantity < suborderProduct.quantity
      );
    };

    // ===== All products integrated in an EDB with same quantities
    const orderedProducts = suborders.flatMap((s) => s.suborderProducts);
    let allProductsSameQties = false;

    if (edbsIntegratedOrders && edbsIntegratedOrders.length > 0) {
      edbsIntegratedOrders.forEach((e) => {
        if (e && e.attributes.orderProducts) {
          allProductsSameQties = orderedProducts.every((p) =>
            hasProductIntegratedWithSameQty(e.attributes.orderProducts, p)
          );
        }
      });
    }

    if (allProductsSameQties) {
      upsertAdvice(
        getAdvice(
          ALL_EQUALS_ADVICE,
          { orderRef: order.reference },
          null,
          order.reference
        )
      );
      return; // No more advice if everything is equal
    }

    // ===== Partially received order: display the previous referencing type
    const partialQtyReceived = suborders.some((suborder) =>
      suborder.suborderProducts.some((sp) =>
        reception.productQuantity.some(
          (pq) =>
            pq.productId === sp.reference && pq.receivedQuantity < sp.quantity
        )
      )
    );
    const partialProductsReceived = suborders.some((suborder) =>
      suborder.suborderProducts.some((sp) =>
        reception.productQuantity.some(
          (pq) => pq.productId === sp.reference && pq.receivedQuantity === 0
        )
      )
    );

    if (partialProductsReceived || partialQtyReceived) {
      // Ensure receptionReferences is an array and find the matching one
      const receptionRefs = normalizeArray(firstReception?.receptionReferences);
      const rr = receptionRefs.find((rr) => {
        const refOrders = normalizeArray(rr.orders);
        return refOrders.some((o) => o.id === order.id);
      });

      if (rr) {
        upsertAdvice(
          getAdvice(
            PARTIAL_RECEPTION_ADVICE,
            {
              orderRef: order.reference,
              refType: rr.referenceType,
            },
            null,
            order.reference
          )
        );
      }
    }

    // ===== By suborder advices
    if (edbsIntegratedOrders && edbsIntegratedOrders.length > 0) {
      suborders.forEach((suborder) => {
        const { suborderProducts } = suborder;
        edbsIntegratedOrders.forEach((e) => {
          if (!e || !e.orderProducts) return;

          const allProductsIntegratedWithSameQty = suborderProducts.every((p) =>
            hasProductIntegratedWithSameQty(e.orderProducts, p)
          );
          const allProductsIntegratedWithLessQty = suborderProducts.every((p) =>
            hasProductIntegratedWithLessQty(e.orderProducts, p)
          );
          const someProductsIntegratedWithSameQty = suborderProducts.some((p) =>
            hasProductIntegratedWithSameQty(e.orderProducts, p)
          );
          const someProductsIntegratedWithLessQty = suborderProducts.some((p) =>
            hasProductIntegratedWithLessQty(e.orderProducts, p)
          );

          let advice = allProductsIntegratedWithSameQty
            ? ALL_PRODUCTS_SAME_QUANTITIES
            : allProductsIntegratedWithLessQty
            ? ALL_PRODUCTS_LESS_QUANTITIES
            : someProductsIntegratedWithSameQty
            ? SOME_PRODUCTS_SAME_QUANTITIES
            : someProductsIntegratedWithLessQty
            ? SOME_PRODUCTS_LESS_QUANTITIES
            : undefined;
          if (!advice) return;

          let suborderRefs = [suborder.reference];
          const isInMultipleSelectedSuborders =
            selectedSuborders?.length > 1 &&
            selectedSuborders.includes(suborder.reference);
          if (
            isInMultipleSelectedSuborders &&
            [
              ALL_PRODUCTS_LESS_QUANTITIES,
              ALL_PRODUCTS_SAME_QUANTITIES,
            ].includes(advice)
          ) {
            advice =
              advice === ALL_PRODUCTS_LESS_QUANTITIES
                ? LESS_QUANTITES_CL_MULTIPLE_SELECTED
                : SAME_QUANTITIES_CL_MULTIPLE_SELECTED;
            suborderRefs = selectedSuborders;
          }
          upsertAdvice(
            getAdvice(
              advice,
              {
                suborderRefs,
              },
              suborder.reference,
              order.reference
            )
          );
        });
      });
    }
  }, [
    upsertAdvice,
    edbsIntegratedOrders,
    firstReception,
    getAdvice,
    order.id,
    order.reference,
    reception.productQuantity,
    selectedSuborders,
    suborders,
  ]);

  if (advices.length === 0) return null;
  return (
    <div className={classes.advices}>
      {advices
        .sort((a1, a2) =>
          (a1.suborderRef ? a1.suborderRef : a1.orderRef).localeCompare(
            a2.suborderRef ? a2.suborderRef : a2.orderRef
          )
        )
        .map((advice, index) => (
          <Alert
            // eslint-disable-next-line react/no-array-index-key
            key={`${advice.orderRef}-${advice.suborderRef || ""}-${index}`}
            severity="info"
          >
            Commande <b>{order.reference}</b>
            {" - "}
            {advice.suborderRef && (
              <>
                <b>{advice.suborderRef}</b>
                {" - "}
              </>
            )}
            {advice.text}
          </Alert>
        ))}
    </div>
  );
}

export default RefTypeAdvice;

const useStyles = createUseStyles(() => ({
  advices: {
    display: "flex",
    flexDirection: "column",
    gap: `${theme.spacing(1)}px`,
  },
}));
